import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { EMAIL_DOC_TYPES, EMAIL_STATUS, EMAIL_TRIGGERS } from "@/lib/salesConstants";
import { formatKr, formatDate, formatDateTime, todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { MailCheck, Search, X, Download, ChevronLeft, ChevronRight, ExternalLink, Paperclip, AlertTriangle, Mail, Loader2, RotateCw, Send } from "lucide-react";

const PAGE_SIZE = 50;

const StatusPill = ({ status, testId }) => {
  const s = EMAIL_STATUS[status] || { label: status || "–", cls: "status-avvist" };
  return <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap", s.cls)} data-testid={testId}>{s.label}</span>;
};

const TypeLabel = ({ type, className }) => {
  const t = EMAIL_DOC_TYPES[type] || { label: type || "–", icon: Mail };
  const Icon = t.icon;
  return <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap", className)}><Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />{t.label}</span>;
};

/** Faktura → E-postlogg: alle fakturarelaterte e-poster sendt fra valgt virksomhet (faktura, kreditnota, purring, inkassovarsel, gjentakende). */
export default function SalesEmailLogPage() {
  const { activeWorkspace } = useAuth();
  const [params, setParams] = useSearchParams();
  const docType = params.get("type") || "alle";
  const status = params.get("status") || "alle";
  const dateFrom = params.get("fra") || "";
  const dateTo = params.get("til") || "";
  const q = params.get("q") || "";
  const page = Math.max(1, Number(params.get("side") || 1));
  const [search, setSearch] = useState(q);
  const [data, setData] = useState(null);
  const [typeCounts, setTypeCounts] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [resendTo, setResendTo] = useState("");
  const [resending, setResending] = useState(""); // log id

  useEffect(() => { setSearch(q); }, [q]);
  useEffect(() => { setResendTo(selected?.to || ""); }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseParams = useMemo(() => ({
    status: status !== "alle" ? status : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    q: q || undefined,
  }), [status, dateFrom, dateTo, q]);

  const load = useCallback(() => {
    setError("");
    setData(null);
    const listParams = { ...baseParams, doc_type: docType !== "alle" ? docType : undefined, page, limit: PAGE_SIZE };
    Promise.all([
      api.get("/sales/email-log", { params: listParams }),
      // Tellere per type uavhengig av valgt type-fane (samme øvrige filtre)
      api.get("/sales/email-log", { params: { ...baseParams, page: 1, limit: 1 } }),
    ])
      .then(([r, c]) => { setData(r.data); setTypeCounts({ alle: c.data.summary.total, ...c.data.summary.by_type }); })
      .catch((e) => setError(errorMessage(e)));
  }, [baseParams, docType, page]);

  useEffect(() => { load(); }, [load]);

  const setParam = (patch) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v); else p.delete(k);
    }
    if (!("side" in patch)) p.delete("side");
    setParams(p, { replace: true });
  };

  const submitSearch = (e) => { e.preventDefault(); setParam({ q: search.trim() }); };
  const hasFilters = !!(q || dateFrom || dateTo || status !== "alle" || docType !== "alle");
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadFile("/sales/email-log/export", `epostlogg_${todayIso()}.csv`, { ...baseParams, doc_type: docType !== "alle" ? docType : undefined });
      toast.success("E-postloggen er lastet ned som CSV");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const wsName = activeWorkspace?.name || "virksomheten";

  /** Send en loggført e-post på nytt (fra detaljpanelet eller direkte fra en feilet rad). */
  const resend = async (entry, to) => {
    if (!entry || resending) return;
    setResending(entry.id);
    try {
      const r = await api.post(`/sales/email-log/${entry.id}/resend`, { to: to || undefined });
      toast.success(`${EMAIL_DOC_TYPES[entry.doc_type]?.label || "E-posten"} sendt på nytt til ${r.data.to}`);
      if (r.data.entry) setSelected(r.data.entry); else setSelected(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
      load();
    } finally {
      setResending("");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Faktura"
        title="E-postlogg"
        description={`Alle fakturaer, kreditnotaer, purringer og inkassovarsler som er sendt på e-post fra ${wsName}. Loggen er kun synlig for denne virksomheten.`}
        actions={<Button variant="outline" onClick={exportCsv} disabled={exporting || !data || data.total === 0} data-testid="email-log-export-button">{exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Last ned CSV</Button>}
      />

      {/* Oppsummering for valgt filter */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="email-log-summary">
        {[
          { key: "total", label: "E-poster", value: summary ? summary.total : null, testId: "email-log-kpi-total" },
          { key: "sendt", label: "Sendt", value: summary ? summary.sendt : null, testId: "email-log-kpi-sent", tone: "text-success" },
          { key: "feilet", label: "Feilet", value: summary ? summary.feilet + summary.ikke_konfigurert : null, testId: "email-log-kpi-failed", tone: summary && summary.feilet + summary.ikke_konfigurert > 0 ? "text-flag" : "" },
          { key: "amount", label: "Sum sendt (inkl. mva)", value: summary ? formatKr(summary.amount_sent) : null, testId: "email-log-kpi-amount" },
        ].map((k) => (
          <Card key={k.key} className="shadow-none">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{k.label}</p>
              {k.value === null ? <Skeleton className="h-7 w-20 mt-1" /> : <p className={cn("font-heading text-2xl font-semibold tnum mt-0.5 whitespace-nowrap", k.tone)} data-testid={k.testId}>{k.value}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtre */}
      <div className="space-y-3">
        <Tabs value={docType} onValueChange={(v) => setParam({ type: v === "alle" ? "" : v })}>
          <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0" data-testid="email-log-type-tabs">
            {[{ value: "alle", label: "Alle" }, ...Object.entries(EMAIL_DOC_TYPES).map(([value, t]) => ({ value, label: t.label }))].map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="rounded-full border data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:border-transparent data-[state=active]:shadow-none px-3 py-1.5 text-sm" data-testid={`email-log-tab-${t.value}`}>
                {t.label}{typeCounts && typeCounts[t.value] !== undefined ? <span className="ml-1.5 text-xs text-muted-foreground tnum">{typeCounts[t.value]}</span> : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <form onSubmit={submitSearch} className="relative w-full lg:w-80" role="search">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk mottaker, kunde, fakturanr, emne…" className="pl-9 pr-9" data-testid="email-log-search-input" aria-label="Søk i e-postloggen" />
            {search ? <button type="button" onClick={() => { setSearch(""); setParam({ q: "" }); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Tøm søk" data-testid="email-log-search-clear"><X className="h-4 w-4" /></button> : null}
          </form>
          <div className="grid grid-cols-2 gap-3 sm:flex sm:items-end">
            <div className="space-y-1">
              <Label htmlFor="elog-from" className="text-xs text-muted-foreground">Fra dato</Label>
              <Input id="elog-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setParam({ fra: e.target.value })} className="h-10 w-full sm:w-40" data-testid="email-log-date-from" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="elog-to" className="text-xs text-muted-foreground">Til dato</Label>
              <Input id="elog-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setParam({ til: e.target.value })} className="h-10 w-full sm:w-40" data-testid="email-log-date-to" />
            </div>
            <div className="space-y-1 col-span-2 sm:col-span-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={status} onValueChange={(v) => setParam({ status: v === "alle" ? "" : v })}>
                <SelectTrigger className="h-10 w-full sm:w-44" data-testid="email-log-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="alle">Alle statuser</SelectItem>
                  {Object.entries(EMAIL_STATUS).map(([v, s]) => <SelectItem key={v} value={v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasFilters ? <Button variant="ghost" size="sm" className="h-10 self-end" onClick={() => setParams({}, { replace: true })} data-testid="email-log-reset-filters"><X className="h-4 w-4 mr-1" /> Nullstill</Button> : null}
        </div>
      </div>

      {error ? (
        <EmptyState icon={AlertTriangle} title="Kunne ikke laste e-postloggen" description={error} actionLabel="Prøv igjen" onAction={load} testId="email-log-error" />
      ) : !data ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={MailCheck} title={hasFilters ? "Ingen treff" : "Ingen e-poster sendt ennå"} description={hasFilters ? "Prøv et annet søk, en annen periode eller nullstill filtrene." : "Når du sender en faktura, kreditnota, purring eller inkassovarsel på e-post fra denne virksomheten, dukker den opp her med mottaker, beløp og status."} actionLabel={hasFilters ? "Nullstill filter" : "Gå til fakturaer"} onAction={hasFilters ? () => setParams({}, { replace: true }) : undefined} actionTo={!hasFilters ? "/faktura/fakturaer" : undefined} testId="email-log-empty" />
      ) : (
        <>
          <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
            <Table data-testid="email-log-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[150px]">Dato og tid</TableHead>
                  <TableHead className="w-[170px]">Type</TableHead>
                  <TableHead>Mottaker</TableHead>
                  <TableHead>Kunde / dokument</TableHead>
                  <TableHead className="text-right w-[130px]">Beløp</TableHead>
                  <TableHead className="w-[130px]">Status</TableHead>
                  <TableHead className="w-[150px]">Sendt av</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer" onClick={() => setSelected(e)} data-testid={`email-log-row-${e.id}`}>
                    <TableCell className="tnum text-muted-foreground whitespace-nowrap">{formatDateTime(e.created_at)}</TableCell>
                    <TableCell><TypeLabel type={e.doc_type} className="font-medium" />{e.doc_type === "inkassovarsel" && e.reminder_level > 1 ? <p className="text-xs text-muted-foreground pl-5">{e.reminder_level}. gang</p> : e.doc_type === "purring" && e.reminder_level ? <p className="text-xs text-muted-foreground pl-5">nr. {e.reminder_level}</p> : null}</TableCell>
                    <TableCell><p className="truncate max-w-[240px]" title={e.to}>{e.to}</p></TableCell>
                    <TableCell>
                      <p className="font-medium truncate max-w-[240px]">{e.customer_name || <span className="text-muted-foreground">Ukjent kunde</span>}</p>
                      {e.invoice_number ? <p className="text-xs text-muted-foreground">{e.invoice_type === "kreditnota" ? "Kreditnota" : "Faktura"} {e.invoice_number}</p> : null}
                    </TableCell>
                    <TableCell className="text-right font-medium tnum whitespace-nowrap">{e.amount !== null && e.amount !== undefined ? formatKr(e.amount) : "–"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <StatusPill status={e.status} testId={`email-log-status-${e.id}`} />
                        {e.status !== "sendt" && e.can_resend && !e.resent_log_id ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(ev) => { ev.stopPropagation(); resend(e); }} disabled={!!resending} aria-label="Send på nytt" title="Send på nytt" data-testid={`email-log-retry-${e.id}`}>
                            {resending === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                          </Button>
                        ) : null}
                      </div>
                      {e.resent_at ? <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`email-log-resent-${e.id}`}>Sendt på nytt {formatDate(e.resent_at)}{e.resent_status && e.resent_status !== "sendt" ? " (feilet)" : ""}</p> : e.resend_of ? <p className="text-[11px] text-muted-foreground mt-0.5">Ny sending</p> : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground"><p className="truncate max-w-[140px]">{e.actor_name || "–"}</p><p className="text-xs">{EMAIL_TRIGGERS[e.trigger] || ""}</p></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="md:hidden space-y-2" data-testid="email-log-cards">
            {data.items.map((e) => (
              <li key={e.id}>
                <button type="button" onClick={() => setSelected(e)} className="w-full text-left rounded-xl border bg-card p-4 card-lift" data-testid={`email-log-card-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <TypeLabel type={e.doc_type} className="font-medium text-sm" />
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{e.to}</p>
                    </div>
                    <StatusPill status={e.status} />
                  </div>
                  <div className="mt-3 flex items-end justify-between gap-3">
                    <p className="text-xs text-muted-foreground truncate">{e.customer_name || "Ukjent kunde"}{e.invoice_number ? ` · ${e.invoice_number}` : ""} · {formatDateTime(e.created_at)}</p>
                    <p className="font-heading font-semibold tnum whitespace-nowrap">{e.amount !== null && e.amount !== undefined ? formatKr(e.amount) : "–"}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground" data-testid="email-log-count">{data.total} {data.total === 1 ? "e-post" : "e-poster"}{totalPages > 1 ? ` · side ${page} av ${totalPages}` : ""}</p>
            {totalPages > 1 ? (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam({ side: String(page - 1) })} data-testid="email-log-prev-page"><ChevronLeft className="h-4 w-4 mr-1" /> Forrige</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setParam({ side: String(page + 1) })} data-testid="email-log-next-page">Neste <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
            ) : null}
          </div>
        </>
      )}

      {/* Detaljer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-card w-full sm:max-w-lg overflow-y-auto" data-testid="email-log-detail">
          {selected ? (
            <>
              <SheetHeader className="text-left">
                <div className="flex flex-wrap items-center gap-2 pr-6">
                  <SheetTitle className="flex items-center gap-2"><TypeLabel type={selected.doc_type} />{selected.invoice_number ? <span className="text-muted-foreground font-normal">· {selected.invoice_number}</span> : null}</SheetTitle>
                  <StatusPill status={selected.status} testId="email-log-detail-status" />
                </div>
                <SheetDescription>{formatDateTime(selected.created_at)}{selected.actor_name ? ` · sendt av ${selected.actor_name}` : ""}{selected.trigger ? ` (${(EMAIL_TRIGGERS[selected.trigger] || selected.trigger).toLowerCase()})` : ""}</SheetDescription>
              </SheetHeader>

              {selected.status !== "sendt" ? (
                <div className="mt-4 rounded-lg border border-[#F9B4B4] dark:border-[#B42318]/50 bg-[#FDE8E8]/60 dark:bg-[#B42318]/10 p-3 text-sm" data-testid="email-log-detail-error">
                  <p className="font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {selected.status === "ikke_konfigurert" ? "E-post var ikke satt opp" : "Sendingen feilet"}</p>
                  {selected.error ? <p className="text-muted-foreground mt-1 break-words">{selected.error}</p> : null}
                  {selected.resent_log_id ? <p className="text-muted-foreground mt-1">Sendt på nytt {formatDateTime(selected.resent_at)}{selected.resent_status && selected.resent_status !== "sendt" ? " – forsøket feilet også." : " – se den nye loggposten."}</p> : null}
                </div>
              ) : selected.resent_at ? (
                <p className="mt-4 text-xs text-muted-foreground" data-testid="email-log-detail-resent-note">Sendt på nytt {formatDateTime(selected.resent_at)}.</p>
              ) : null}
              {selected.resend_of ? (
                <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="email-log-detail-resend-of"><RotateCw className="h-3.5 w-3.5" /> Ny sending av e-post fra {formatDateTime(selected.resend_of_created_at)}.</p>
              ) : null}

              <dl className="mt-5 grid grid-cols-[120px_1fr] gap-x-4 gap-y-3 text-sm" data-testid="email-log-detail-fields">
                <dt className="text-muted-foreground">Mottaker</dt><dd className="break-all font-medium">{selected.to}</dd>
                <dt className="text-muted-foreground">Avsender</dt><dd className="break-all">{selected.from_name ? `${selected.from_name} <${selected.from_email || ""}>` : selected.from_email || "–"}</dd>
                <dt className="text-muted-foreground">Svar til</dt><dd className="break-all">{selected.reply_to || "–"}</dd>
                <dt className="text-muted-foreground">Emne</dt><dd>{selected.subject || "–"}</dd>
                <dt className="text-muted-foreground">Kunde</dt><dd>{selected.customer_name || "–"}</dd>
                {selected.issuer_name ? <><dt className="text-muted-foreground">Fra virksomhet</dt><dd>{selected.issuer_name}</dd></> : null}
                <dt className="text-muted-foreground">Beløp</dt><dd className="tnum font-medium">{selected.amount !== null && selected.amount !== undefined ? formatKr(selected.amount) : "–"}{selected.doc_type === "purring" || selected.doc_type === "inkassovarsel" ? <span className="text-muted-foreground font-normal"> å betale</span> : null}</dd>
                {selected.fee ? <><dt className="text-muted-foreground">Gebyr</dt><dd className="tnum">{formatKr(selected.fee)}</dd></> : null}
                {selected.due_date ? <><dt className="text-muted-foreground">Forfall</dt><dd className="tnum">{formatDate(selected.due_date)}</dd></> : null}
                {selected.new_due_date ? <><dt className="text-muted-foreground">Ny frist</dt><dd className="tnum">{formatDate(selected.new_due_date)}</dd></> : null}
                {selected.reminder_level ? <><dt className="text-muted-foreground">{selected.doc_type === "inkassovarsel" ? "Varsel nr." : "Purring nr."}</dt><dd className="tnum">{selected.reminder_level}</dd></> : null}
                <dt className="text-muted-foreground">Vedlegg</dt>
                <dd>{selected.attachments?.length ? <ul className="space-y-1">{selected.attachments.map((a) => <li key={a} className="flex items-center gap-1.5 break-all"><Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />{a}</li>)}</ul> : "–"}</dd>
                {selected.message ? <><dt className="text-muted-foreground">Melding</dt><dd className="whitespace-pre-wrap break-words">{selected.message}</dd></> : null}
              </dl>

              {selected.can_resend ? (
                <div className="mt-6 rounded-lg border p-3 space-y-2" data-testid="email-log-resend-section">
                  <p className="text-sm font-medium flex items-center gap-2"><RotateCw className="h-4 w-4 text-muted-foreground" /> Send på nytt</p>
                  <p className="text-xs text-muted-foreground">{selected.doc_type === "purring" || selected.doc_type === "inkassovarsel" ? "Samme purring sendes igjen med samme gebyr og frist – det registreres ikke en ny purring." : "Dokumentet sendes igjen som PDF med samme melding."} Alle forsøk logges.</p>
                  <form className="flex flex-col sm:flex-row gap-2" onSubmit={(ev) => { ev.preventDefault(); resend(selected, resendTo.trim()); }}>
                    <Input type="email" value={resendTo} onChange={(ev) => setResendTo(ev.target.value)} placeholder="mottaker@firma.no" className="h-10" aria-label="Mottaker for ny sending" data-testid="email-log-resend-to" />
                    <Button type="submit" variant={selected.status !== "sendt" ? "default" : "outline"} className="h-10 shrink-0" disabled={!!resending || !resendTo.includes("@")} data-testid="email-log-resend-button">
                      {resending === selected.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Send på nytt
                    </Button>
                  </form>
                </div>
              ) : null}

              {selected.invoice_id ? (
                <Button asChild variant="outline" className="mt-3 w-full" data-testid="email-log-detail-open-invoice">
                  <Link to={`/faktura/fakturaer/${selected.invoice_id}`}><ExternalLink className="h-4 w-4 mr-2" /> Åpne {selected.invoice_type === "kreditnota" ? "kreditnota" : "faktura"} {selected.invoice_number || ""}</Link>
                </Button>
              ) : null}
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
